/**
 * The run, as a list of constant-acceleration phases.
 *
 * Between events every acceleration in this engine is constant: the masses do
 * not change, gravity does not change, and the rope directions do not change
 * while the arrangement is direction-invariant. So the motion is not something
 * to approximate frame by frame — it is
 *
 *     q(t) = q₀ + v₀·t + ½·a·t²        v(t) = v₀ + a·t
 *
 * and it holds exactly until something HAPPENS. Building the run is therefore:
 * solve; ask every inactive row WHEN it would become active (each row's gap is
 * a quadratic in t, so each answer is a smallest positive root); take the
 * earliest; resolve the impact; repeat.
 *
 * What that buys, and none of it is available from a fixed tick: `at(t)` for
 * any t in constant time, so a reader can scrub the run or jump to 3.4 s; a
 * list of events with their exact times — "M₂ lands at t = 1.42 s"; no drift,
 * because there is nothing to accumulate; and a run that does not depend on
 * frame rate, on how long a tab was backgrounded, or on anything the student
 * did not choose. A student's own answer becomes something the bench can
 * confirm or contradict to the digit.
 */
import { constraintsOf } from './constraints';
import { checkNewton, freeBodies, FreeBody } from './freeBody';
import { firstDownwardRoot, solveFree } from './linalg';
import { GRAM, isExact, solve, Solution } from './solve';
import { defOf } from './registry';
import { Body, Constraint, World } from './types';
import { bodiesOf, isFree, partById, ropesOf, segmentsOf } from './world';
import { reconcile } from './reconcile';

export interface RunEvent {
    /** Seconds from the start of the run. */
    at: number;
    /** What became true. */
    kind: 'taut' | 'landed' | 'met' | 'stopped';
    constraintId: string;
    /** Said in words, for a timeline a reader can read. */
    text: string;
}

export interface Phase {
    from: number;
    /** Infinity for the last one. */
    to: number;
    /** Constant through the phase — the whole point. */
    acceleration: Record<string, number[]>;
    q0: Record<string, number[]>;
    v0: Record<string, number[]>;
    force: Record<string, number>;
    active: string[];
    freeBodies: FreeBody[];
    endedBy?: RunEvent;
}

export interface Run {
    phases: Phase[];
    events: RunEvent[];
    /** False when the arrangement is not direction-invariant, so the phases
     *  are an approximation and the engine says so instead of pretending. */
    exact: boolean;
    singular: boolean;
    /** Bodies, in the order their coordinates appear. */
    bodies: Body[];
    /** Anything Newton's second law says is wrong with the solve. Empty in a
     *  healthy run; a test asserts that. */
    residuals: string[];
}

/** State of every body at some instant. */
export interface Snapshot {
    t: number;
    /** Body id → displacement along each axis, from where the run started. */
    q: Record<string, number[]>;
    v: Record<string, number[]>;
    acceleration: Record<string, number[]>;
    force: Record<string, number>;
    freeBodies: FreeBody[];
    phase: number;
}

const MAX_PHASES = 64;

/**
 * How long a step may be where the geometry TURNS as it moves.
 *
 * A phase is exact because the acceleration is constant through it, and the
 * acceleration is constant because the rope directions are. Where they are
 * not — a pulley swinging between two hooks — that is false the instant
 * anything moves, so the phase is cut short, the bench is solved again at the
 * new arrangement, and the run becomes a chain of short exact phases instead
 * of one long wrong one. `Run.exact` stays false, and now means "stepped"
 * rather than "quietly approximate".
 *
 * FIVE MILLISECONDS, chosen by measuring rather than by taste. The error a
 * step leaves in a rope's length is very nearly proportional to the step, so
 * halving it halves the drift, and the whole scale on a two-pulley swing is:
 *
 *     0.02  →  6 mm out after a quarter second       258 phases
 *     0.01  →  5 mm                                   77
 *     0.005 →  2 mm                                  343
 *     0.002 →  0.8 mm                               2815
 *
 * Two millimetres on a six-metre bench is narrower than the drawn rope, and
 * the next step down costs eight times the phases to save one more. Being
 * proportional is the thing worth knowing: it says the error is the stepping
 * itself and not something structurally wrong, which a drift that refused to
 * shrink would have meant.
 */
const STEP = 0.005;

/** How far a stepped run is followed before it is left unfinished. A closed
 *  form costs one solve however long it lasts; a stepped one costs a solve
 *  every hundredth of a second, and a reader watching a swing does not need
 *  ten minutes of it. */
const STEP_HORIZON = 5;

/** The loop bound. Events are still capped at `MAX_PHASES`; the rest of these
 *  are steps, which are cheap to make and many. */
const MAX_TURNS = 4096;

/*
 * ── Zeno ──────────────────────────────────────────────────────────────────
 *
 * A weight landing under a rope that can go slack really does chatter. It
 * lands; its partner sails on and falls back; the rope snaps taut and jerks
 * the weight off the floor again — a quarter as high, then a sixteenth. The
 * events pile up at an accumulation point and never quite arrive, so an engine
 * that simply keeps finding the next one never finishes. That is Zeno's
 * problem, and it is the physics being right rather than the solver being
 * wrong: the motion dies away in a FINITE time after infinitely many bounces.
 *
 * So find that time rather than walking towards it. Once the same event has
 * come round three times with the interval shrinking by a steady ratio, the
 * rest of the series is a geometric tail and sums in closed form:
 *
 *     remaining = (dtₙ₋₁ + dtₙ) · r / (1 − r)
 *
 * The clock jumps to the end of that, the velocities go to zero, and the whole
 * shudder is reported as ONE event — "M₁ settles on the floor" — because that
 * is what a reader saw happen. The tail becomes a phase in its own right, at
 * rest: what is given up is the jiggle within it, and CYCLE_SPEED is there to
 * make sure that jiggle is already smaller than a line is wide before the
 * engine stops following it. Stretching the last parabola over the tail
 * instead would have flung a weight a third of a metre.
 *
 * MIN_PHASE is the net beneath that: a chatter which is not geometric (three
 * rows taking turns, say) still has to stop somewhere.
 */
const CYCLE_RATIO = 0.95;
const CYCLE_AGREE = 0.1;
const CYCLE_CONFIRM = 3;
/** m/s still in the bounce when it is fair to call the thing stopped. */
const CYCLE_SPEED = 0.05;
const MIN_PHASE = 2e-3;
const MIN_PHASE_RUN = 4;
const TOL = 1e-9;
/** How fast, in m/s, something must reach the floor for it to have LANDED
 *  rather than settled back onto it. A load let go from any height a bench
 *  draws arrives at tenths of a metre a second or more; a block the rope has
 *  only rocked on the boards comes back at thousandths. */
const LANDING_SPEED = 0.02;

/** Move a world's parts by each body's displacement, so it can be re-solved. */
export function displaced(w: World, bodies: Body[], q: Record<string, number[]>): World {
    const move = new Map<string, { x: number; y: number }>();
    bodies.filter(isFree).forEach(b => {
        const d = q[b.id] ?? [];
        const off = b.axes.reduce(
            (s, axis, i) => ({ x: s.x + axis.x * (d[i] ?? 0), y: s.y + axis.y * (d[i] ?? 0) }),
            { x: 0, y: 0 },
        );
        b.partIds.forEach(id => move.set(id, off));
    });
    return {
        ...w,
        parts: w.parts.map(p => {
            const off = move.get(p.id);
            return off ? { ...p, at: { x: p.at.x + off.x, y: p.at.y + off.y } } : p;
        }),
    };
}

/**
 * An inelastic impulse that removes the approach speed along the newly active
 * rows.
 *
 * A rope snapping taut does not bounce, and neither does a weight landing on a
 * floor. Solved the same way the accelerations are — the same KKT shape one
 * level down, and the SAME active-set loop, because an impulse is one-sided
 * too: a rope can jerk a weight to a stop but it cannot shove one.
 *
 * That loop is not decoration. When one weight of an Atwood lands, satisfying
 * the floor AND the rope together would stop its partner dead — the rope would
 * have to push it downwards. Dropping the row whose impulse comes out negative
 * is exactly the moment the rope goes slack and the partner sails on, which is
 * the behaviour the old engine could not produce at all.
 */
function resolveImpact(
    bodies: Body[],
    constraints: Constraint[],
    active: string[],
    v: Record<string, number[]>,
    bounce: number,
): Record<string, number[]> {
    /* Friction takes no impulse — a surface's grip is a force over time and
     * there is no time here. A two-sided row takes one and never gives it up:
     * a table does not let go of a block because the arithmetic came out
     * negative, the way a rope lets go when it would have to push. */
    const rows0 = active
        .map(id => constraints.find(x => x.id === id))
        .filter((c): c is Constraint => !!c && c.kind !== 'friction');
    const coords: { bodyId: string; axis: number; mass: number }[] = [];
    bodies.filter(isFree).forEach(b => b.axes.forEach((_, i) => coords.push({ bodyId: b.id, axis: i, mass: b.mass })));
    const idx = new Map(coords.map((c, i) => [`${c.bodyId}#${c.axis}`, i]));
    const n = coords.length;
    const rows = rows0.map(c => {
        const r = new Array<number>(n).fill(0);
        c.rows.forEach(x => {
            const i = idx.get(`${x.coord.bodyId}#${x.coord.axis}`);
            if (i !== undefined) r[i] += x.coefficient;
        });
        return r;
    });
    if (!rows.length || !n) return v;
    const v0 = coords.map(c => v[c.bodyId]?.[c.axis] ?? 0);

    /* How fast each row is allowed to be OPENING when the impact is over.
     *
     * Zero is a dead landing and is what an inextensible rope always gets — a
     * rope that bounced would be a bungee. A contact keeps `e` of whatever
     * speed it arrived with, so `e = 0.5` leaves a quarter of the height,
     * which is the number a book states and a student can measure. */
    const want = rows.map((row, k) => {
        const c = rows0[k];
        if (c.kind !== 'contact' && c.kind !== 'separation') return 0;
        const closing = Math.min(0, row.reduce((s, r, i) => s + r * v0[i], 0));
        return -bounce * closing;
    });

    let keep = rows.map((_, k) => k);
    for (let pass = 0; pass < 40; pass++) {
        const use = keep.map(k => rows[k]);
        const m = use.length;
        const size = n + m;
        const A: number[][] = Array.from({ length: size }, () => new Array(size).fill(0));
        const b = new Array<number>(size).fill(0);
        for (let i = 0; i < n; i++) {
            /* The same gram of inertia the acceleration solve lends a massless
             * coordinate, and for the same reason: `m·Δv = impulse` says
             * nothing when `m` is zero, so one massless part anywhere in the
             * system made this matrix singular. `solveLinear` then returned
             * null and this function gave up — silently, handing back the
             * velocity it was given. The landing was never resolved: a weight
             * arrived at the floor doing 20 m/s, stayed at 20 m/s, buried
             * itself 3 mm into the ground and re-landed on every step, 181
             * times, while the pulleys wandered off sideways. */
            const mass = coords[i].mass > 1e-12 ? coords[i].mass : GRAM;
            A[i][i] = mass;
            b[i] = mass * v0[i];
            for (let k = 0; k < m; k++) A[i][n + k] = -use[k][i];
        }
        for (let k = 0; k < m; k++) {
            for (let i = 0; i < n; i++) A[n + k][i] = use[k][i];
            b[n + k] = want[keep[k]];
        }
        /* `solveFree`, so a coordinate nothing constrains keeps the velocity
         * it had rather than aborting the whole impact. Giving up here is the
         * worst of the three outcomes: it looks like nothing happened. */
        const x = solveFree(A, b);
        if (!x) return v;

        // An impulse that would have to PUSH is not one this row can deliver:
        // drop it, and whatever it was holding carries on.
        const P = x.slice(n);
        let worst = -1, worstVal = -1e-9;
        P.forEach((val, k) => {
            if (!rows0[keep[k]].oneSided) return;
            if (val < worstVal) { worstVal = val; worst = k; }
        });
        if (worst >= 0) { keep = keep.filter((_, k) => k !== worst); continue; }

        const out: Record<string, number[]> = {};
        bodies.filter(isFree).forEach(bd => {
            out[bd.id] = bd.axes.map((_, i) => x[idx.get(`${bd.id}#${i}`) as number]);
        });
        return out;
    }
    return v;
}

const eventText = (c: Constraint, name: (id?: string) => string): { kind: RunEvent['kind']; text: string } => {
    if (c.kind === 'rope') return { kind: 'taut', text: `${c.label ?? 'the rope'} goes taut` };
    if (c.kind === 'separation') return { kind: 'met', text: `${c.label ?? 'two parts on one rope'} meet` };
    if (c.kind === 'friction') return { kind: 'stopped', text: `${name(c.sourceId)} comes to rest` };
    if (c.label === 'the floor') return { kind: 'landed', text: `${name(c.sourceId)} reaches the floor` };
    if (c.label === 'the ceiling') return { kind: 'stopped', text: `${name(c.sourceId)} reaches the beam` };
    if (c.label) return { kind: 'stopped', text: `${name(c.sourceId)} reaches ${c.label}` };
    return { kind: 'stopped', text: `${name(c.sourceId)} stops` };
};

export function buildRun(world: World): Run {
    const bodies = bodiesOf(world);
    const nameOf = (partId?: string): string => {
        const p = partId ? partById(world, partId) : undefined;
        return p ? defOf(p).name(p as never, world) : 'a part';
    };

    const phases: Phase[] = [];
    const events: RunEvent[] = [];
    const residuals: string[] = [];

    let t = 0;
    let q: Record<string, number[]> = {};
    let v: Record<string, number[]> = {};
    bodies.filter(isFree).forEach(b => { q[b.id] = b.axes.map(() => 0); v[b.id] = b.axes.map(() => 0); });

    let exact = true;
    let singular = false;
    let shudders = 0;
    /* The recent events and how the intervals between them are shrinking —
     * everything needed to recognise a dying oscillation and sum its tail. */
    let recent: { id: string; dt: number }[] = [];
    let ratios: number[] = [];
    let chatterAt = -1;
    /** Set when a settled chatter has moved the clock on: the phase that comes
     *  next covers the tail too, and so begins where the last bounce ended. */
    let restFrom: number | null = null;

    for (let n = 0; n < MAX_TURNS; n++) {
        const w = displaced(world, bodies, q);
        const live = bodiesOf(w);
        const cons = constraintsOf(w, live);
        const sol = solve({ world: w, bodies: live, constraints: cons, velocity: v });
        if (sol.singular) { singular = true; break; }

        /* Asked AFTER the solve, and of the MOTION rather than of the
         * coordinates. A run stays straight while nothing at its ends moves
         * across it, and whether anything does is what the solve has just
         * worked out. Asked of the axes instead — are they all parallel to the
         * run — it answered "no" for every bench the moment parts were given a
         * sideways coordinate, whether or not anything ever used it. */
        const straight = isExact(w, live, sol, v);
        exact = exact && straight;

        const fbs = freeBodies(w, live, cons, sol);
        residuals.push(...checkNewton(live, fbs, sol));

        const a = sol.acceleration;

        // When does the next thing happen? Every inactive row's gap is a
        // quadratic in t, so the question has a closed answer.
        let next: { at: number; c: Constraint } | null = null;
        cons.forEach(c => {
            if (sol.active.includes(c.id)) return;
            let g0 = c.gap, g1 = 0, g2 = 0;
            c.rows.forEach(r => {
                g1 += r.coefficient * (v[r.coord.bodyId]?.[r.coord.axis] ?? 0);
                g2 += r.coefficient * (a[r.coord.bodyId]?.[r.coord.axis] ?? 0);
            });
            if (c.kind === 'friction') {
                /* A sliding body coming to REST is an event, and the row's own
                 * gap says nothing about it — a friction row has no gap, it has
                 * a grip. What happens at that instant is the chapter's whole
                 * point: the drag that was slowing the body may now hold it
                 * still, or may not, and only the next solve knows which. */
                const stop = Math.abs(g2) > 1e-12 ? -g1 / g2 : null;
                if (stop !== null && stop > TOL && (!next || stop < next.at)) next = { at: stop, c };
                return;
            }
            const root = firstDownwardRoot(g0, g1, g2);
            if (root !== null && (!next || root < next.at)) next = { at: root, c };
        });

        const phase: Phase = {
            from: restFrom ?? t,
            to: next ? t + (next as { at: number }).at : Infinity,
            acceleration: a,
            q0: { ...q },
            v0: { ...v },
            force: sol.force,
            active: sol.active,
            freeBodies: fbs,
        };

        restFrom = null;

        /* ── a STEP, where the closed form has run out of validity ──────────
         *
         * The phase above is exact for an instant and no longer, because the
         * runs it was solved with turn as things move. So it is cut to `STEP`,
         * the state is carried forward by that much, and the loop comes round
         * and solves the new arrangement. No event has happened — nothing
         * became true — so nothing is recorded and none of the bookkeeping
         * below (impacts, chatter, settling) applies.
         *
         * And then the lengths are PUT BACK. Stepping satisfies the ropes at
         * the acceleration level only, so each step leaves them a little
         * wrong and the errors add up; `reconcile` is the position-level solve
         * that already exists for exactly this, and one call a step keeps a
         * rope the length it is. */
        const evAt = next ? (next as { at: number }).at : Infinity;
        if (!straight && STEP < evAt) {
            if (t >= STEP_HORIZON) { phases.push(phase); break; }
            phase.to = t + STEP;
            phases.push(phase);

            const qs: Record<string, number[]> = {};
            const vs: Record<string, number[]> = {};
            live.filter(isFree).forEach(b => {
                qs[b.id] = b.axes.map((_, i) =>
                    (q[b.id]?.[i] ?? 0) + (v[b.id]?.[i] ?? 0) * STEP + 0.5 * (a[b.id]?.[i] ?? 0) * STEP * STEP);
                vs[b.id] = b.axes.map((_, i) => (v[b.id]?.[i] ?? 0) + (a[b.id]?.[i] ?? 0) * STEP);
            });
            const fixed = reconcile(displaced(world, bodies, qs), new Set());
            q = fixed ? readBack(world, bodies, fixed, qs) : qs;
            v = vs;
            t += STEP;
            continue;
        }

        if (!next) { phases.push(phase); break; }

        const dt = (next as { at: number }).at;
        const c = (next as { c: Constraint }).c;
        const ev = eventText(c, nameOf);
        const event: RunEvent = { at: t + dt, kind: ev.kind, constraintId: c.id, text: ev.text };
        phase.endedBy = event;
        phases.push(phase);
        events.push(event);

        // Advance exactly to the event, then take the bounce out of it.
        const q2: Record<string, number[]> = {};
        const v2: Record<string, number[]> = {};
        live.filter(isFree).forEach(b => {
            q2[b.id] = b.axes.map((_, i) =>
                (q[b.id]?.[i] ?? 0) + (v[b.id]?.[i] ?? 0) * dt + 0.5 * (a[b.id]?.[i] ?? 0) * dt * dt);
            v2[b.id] = b.axes.map((_, i) => (v[b.id]?.[i] ?? 0) + (a[b.id]?.[i] ?? 0) * dt);
        });
        q = q2;
        /* How fast it closed on whatever it met — read BEFORE the impact takes
         * it out. A landing at a whisker of speed is a part that was already
         * resting there, not one arriving (see LANDING_SPEED). */
        const closing = Math.abs(c.rows.reduce((sum, row) =>
            sum + row.coefficient * (v2[row.coord.bodyId]?.[row.coord.axis] ?? 0), 0));
        v = resolveImpact(live, cons, [...sol.active, c.id], v2, world.restitution ?? 0);
        /* Exactly at rest along the face — that IS the event. Rounding can
         * leave a whisker of speed, and a whisker is enough for the next solve
         * to call the body "sliding" and hand it kinetic friction for ever. */
        if (c.kind === 'friction') {
            c.rows.forEach(r => { const row = v[r.coord.bodyId]; if (row) row[r.coord.axis] = 0; });
        }
        t += dt;

        /* Is this event one we have seen before, arriving sooner each time? The
         * comparison is with the event two back rather than one, because a
         * cycle has two halves and only whole cycles shrink by a fixed ratio. */
        recent.push({ id: c.id, dt });
        const prior = recent[recent.length - 3];
        const r = prior && prior.id === c.id && prior.dt > TOL ? dt / prior.dt : null;
        if (r !== null && r < CYCLE_RATIO) {
            if (!ratios.length) chatterAt = events.length - 1;
            ratios.push(r);
        } else {
            ratios = [];
            chatterAt = -1;
        }

        const slow = Object.values(v).every(row => row.every(x => Math.abs(x) < CYCLE_SPEED));
        const steady = r !== null && slow && ratios.length >= CYCLE_CONFIRM
            && ratios.slice(-CYCLE_CONFIRM).every(x => Math.abs(x - r) <= CYCLE_AGREE * r);

        if (steady && r !== null) {
            // Sum the rest of the series and go straight to where it ends. The
            // phase that follows starts HERE and covers the tail at rest.
            restFrom = t;
            t += (dt + (recent[recent.length - 2]?.dt ?? 0)) * r / (1 - r);
            Object.keys(v).forEach(k => { v[k] = v[k].map(() => 0); });

            const resting = [c.id, recent[recent.length - 2]?.id]
                .map(id => cons.find(x => x.id === id))
                .find(x => x && x.kind === 'contact');
            const settle: RunEvent = {
                at: t,
                kind: 'stopped',
                constraintId: resting?.id ?? c.id,
                text: resting
                    ? `${nameOf(resting.sourceId)} settles on ${resting.label ?? 'the floor'}`
                    : 'the motion dies away',
            };
            // The bounces were one thing happening, so say it once.
            events.length = chatterAt;
            events.push(settle);
            phases[phases.length - 1].endedBy = settle;
            recent = [];
            ratios = [];
            chatterAt = -1;
            shudders = 0;
        } else {
            // Not geometric, but still going nowhere: stop it anyway.
            shudders = dt < MIN_PHASE ? shudders + 1 : 0;
            if (shudders >= MIN_PHASE_RUN) {
                Object.keys(v).forEach(k => { v[k] = v[k].map(() => 0); });
                shudders = 0;
            }
        }

        /* SOMETHING LANDED: the run is over.
         *
         * Not because the arithmetic gives up, but because the question does.
         * A pulley machine is asked "what does it do when you let it go", and
         * the answer runs from the release to the first thing reaching the
         * floor — that is the interval a reader times, the one a textbook
         * gives a formula for, and the one this engine answers exactly.
         *
         * What comes after it is a different and much worse problem: a rope
         * that has gone slack while its far end is still moving, a load
         * bouncing on the boards, and a weightless wheel with nothing left to
         * hold it steady. The bench used to carry on into all of that and show
         * it with the same confidence as the part it had actually solved.
         * Stopping is the honest edge of the answer.
         *
         * UNLESS the bench was given bounce. A restitution is a reader saying
         * "what happens after it lands is the thing I want to see", and on
         * such a bench the ground is not the end of the story but the middle
         * of it — the whole demonstration is the height it comes back to.
         * Every pulley machine has restitution zero and stops; a dropped ball
         * carries on, and its bounces are already gathered into one event by
         * the chatter test further down.
         *
         * And only on a bench with ROPE on it. The reason to stop is that a
         * rope goes slack the moment its load lands, and everything after that
         * is the part this engine answers least well. A block sliding down a
         * ramp and coming to rest under friction has no rope in the story and
         * no such cliff: stopping it at the floor would delete a whole chapter
         * to solve a problem it does not have.
         */
        /* And only a real arrival. A block already standing on the floor can
         * be lifted a few microns by a rope pulling at an angle and set back
         * down a hundredth of a second later; that touched the floor as surely
         * as a falling load does, and it ended every run it was in — the whole
         * machine frozen at 0.03 s with the load still in the air. Below
         * LANDING_SPEED it is the contact coming back, and the run goes on. */
        if (ev.kind === 'landed' && closing >= LANDING_SPEED && !(world.restitution ?? 0) && ropesOf(world).length) {
            Object.keys(v).forEach(k => { v[k] = v[k].map(() => 0); });
            phases.push({
                from: t,
                to: Infinity,
                acceleration: Object.fromEntries(Object.keys(q).map(k => [k, q[k].map(() => 0)])),
                q0: { ...q },
                v0: { ...v },
                force: {},
                active: [],
                freeBodies: [],
            });
            break;
        }

        // Everything has stopped and nothing is pushing: the run is over.
        const moving = Object.values(v).some(row => row.some(x => Math.abs(x) > 1e-7));
        const accelerating = Object.values(a).some(row => row.some(x => Math.abs(x) > 1e-7));
        if (!moving && !accelerating) break;
    }

    return { phases, events, exact, singular, bodies, residuals };
}

/** Where everything is, and how fast, at any instant. Constant time. */
export function at(run: Run, t: number): Snapshot | null {
    if (!run.phases.length) return null;
    const clamped = Math.max(0, t);
    let i = run.phases.findIndex(p => clamped >= p.from && clamped < p.to);
    if (i < 0) i = run.phases.length - 1;
    const p = run.phases[i];
    const dt = clamped - p.from;
    const q: Record<string, number[]> = {};
    const v: Record<string, number[]> = {};
    Object.keys(p.q0).forEach(id => {
        q[id] = p.q0[id].map((q0, k) => q0 + (p.v0[id]?.[k] ?? 0) * dt + 0.5 * (p.acceleration[id]?.[k] ?? 0) * dt * dt);
        v[id] = p.v0[id].map((v0, k) => v0 + (p.acceleration[id]?.[k] ?? 0) * dt);
    });
    return { t: clamped, q, v, acceleration: p.acceleration, force: p.force, freeBodies: p.freeBodies, phase: i };
}

/** The world as it is at an instant — what the bench draws. */
/**
 * Where each body has got to, as coordinates, read off a world.
 *
 * The inverse of `displaced`, for the one place that needs it: a stepped run
 * puts its positions back with `reconcile`, which answers in a WORLD, and the
 * run carries coordinates. Exact while a body's axes are perpendicular unit
 * vectors, which they are — the plane's, a face's, a beam's.
 */
function readBack(
    world: World,
    bodies: Body[],
    fixed: World,
    fallback: Record<string, number[]>,
): Record<string, number[]> {
    const out: Record<string, number[]> = {};
    bodies.filter(isFree).forEach(b => {
        const id = b.partIds[0];
        const was = partById(world, id), now = partById(fixed, id);
        if (!was || !now) { out[b.id] = fallback[b.id]; return; }
        const d = { x: now.at.x - was.at.x, y: now.at.y - was.at.y };
        out[b.id] = b.axes.map(ax => ax.x * d.x + ax.y * d.y);
    });
    return out;
}

export function worldAt(world: World, run: Run, t: number): World {
    const s = at(run, t);
    return s ? displaced(world, run.bodies, s.q) : world;
}

/** How long the run lasts before nothing further happens. */
export const runDuration = (run: Run): number =>
    run.events.length ? run.events[run.events.length - 1].at : 0;
